import { useState } from 'react';
import {
  ChevronDown, Languages, Globe, Calculator, Brain,
  Target, Clock, Shield, AlertTriangle, Users, CheckCircle,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';

const CDS_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "CDS Syllabus 2026 – Complete IMA, INA, AFA & OTA Topics",
  "description": "Complete CDS 2026 syllabus from official UPSC notification. English (100 marks), General Knowledge (100 marks), Elementary Mathematics (100 marks, IMA/INA/AFA only). SSB interview details, negative marking rules, and OTA-specific pattern.",
  "author": { "@type": "Organization", "name": "EduPulseAI" },
  "publisher": { "@type": "Organization", "name": "EduPulseAI", "url": "https://edupulseai.co.in" },
  "datePublished": "2026-08-20",
  "dateModified": "2026-08-20",
  "mainEntityOfPage": { "@type": "WebPage", "@id": "https://edupulseai.co.in/syllabus/cds" },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://edupulseai.co.in" },
      { "@type": "ListItem", "position": 2, "name": "CDS Syllabus 2026" }
    ]
  }
};

/* ── Exam scheme ───────────────────────────────────────────────────────── */
const IMA_INA_AFA = [
  { no: 1, subject: 'English',               code: 11, duration: '2 Hours', marks: 100, color: '#10B981' },
  { no: 2, subject: 'General Knowledge',     code: 12, duration: '2 Hours', marks: 100, color: '#6366F1' },
  { no: 3, subject: 'Elementary Mathematics',code: 13, duration: '2 Hours', marks: 100, color: '#F59E0B' },
];

const OTA = [
  { no: 1, subject: 'English',           code: 11, duration: '2 Hours', marks: 100, color: '#10B981' },
  { no: 2, subject: 'General Knowledge', code: 12, duration: '2 Hours', marks: 100, color: '#6366F1' },
];

/* ── Math topics ───────────────────────────────────────────────────────── */
const MATH_TOPICS = [
  {
    id: 'arithmetic', title: 'Arithmetic', color: '#F59E0B',
    points: [
      'Number System — Natural numbers, Integers, Rational and Real numbers.',
      'Fundamental operations: addition, subtraction, multiplication, division. Square roots. Decimal fractions.',
      'Unitary method, time and distance, time and work, percentages.',
      'Applications to simple and compound interest, profit and loss, ratio and proportion, variation.',
      'Elementary Number Theory — Division algorithm. Prime and composite numbers.',
      'Tests of divisibility by 2, 3, 4, 5, 9 and 11. Multiples and factors. Factorisation Theorem. H.C.F. and L.C.M.',
      'Euclidean algorithm. Logarithms to base 10, laws of logarithms, use of logarithmic tables.',
    ],
  },
  {
    id: 'algebra', title: 'Algebra', color: '#6366F1',
    points: [
      'Basic Operations, simple factors, Remainder Theorem, H.C.F., L.C.M., Theory of polynomials.',
      'Solutions of quadratic equations, relation between its roots and coefficients (Only real roots to be considered).',
      'Simultaneous linear equations in two unknowns — analytical and graphical solutions.',
      'Simultaneous linear inequations in two variables and their solutions.',
      'Practical problems leading to two simultaneous linear equations or inequations in two variables or quadratic equations in one variable & their solutions.',
      'Set language and set notation, Rational expressions and conditional identities, Laws of indices.',
    ],
  },
  {
    id: 'trigonometry', title: 'Trigonometry', color: '#EC4899',
    points: [
      'Sine x, cosine x, Tangent x when 0° ≤ x ≤ 90°.',
      'Values of sin x, cos x and tan x, for x = 0°, 30°, 45°, 60° and 90°.',
      'Simple trigonometric identities.',
      'Use of trigonometric tables.',
      'Simple cases of heights and distances.',
    ],
  },
  {
    id: 'geometry', title: 'Geometry', color: '#3B82F6',
    points: [
      'Lines and angles, Plane and plane figures.',
      'Theorems on: (i) Properties of angles at a point.',
      '(ii) Parallel lines.',
      '(iii) Sides and angles of a triangle.',
      '(iv) Congruency of triangles.',
      '(v) Similar triangles.',
      '(vi) Concurrence of medians and altitudes.',
      '(vii) Properties of angles, sides and diagonals of a parallelogram, rectangle and square.',
      '(viii) Circles and its properties including tangents and normals.',
      '(ix) Loci.',
    ],
  },
  {
    id: 'mensuration', title: 'Mensuration', color: '#14B8A6',
    points: [
      'Areas of squares, rectangles, parallelograms, triangle and circle.',
      'Areas of figures which can be split up into these figures (Field Book).',
      'Surface area and volume of cuboids.',
      'Lateral surface and volume of right circular cones and cylinders.',
      'Surface area and volume of spheres.',
    ],
  },
  {
    id: 'statistics', title: 'Statistics', color: '#8B5CF6',
    points: [
      'Collection and tabulation of statistical data.',
      'Graphical representation — frequency polygons, histograms, bar charts, pie charts etc.',
      'Measures of central tendency.',
    ],
  },
];

/* ── SSB stages ────────────────────────────────────────────────────────── */
const SSB_STAGES = [
  {
    stage: 'Stage I', color: '#6366F1',
    items: [
      'Officer Intelligence Rating (OIR) Tests.',
      'Picture Perception & Description Test (PP&DT).',
      'Candidates shortlisted based on combined performance in OIR Test and PP&DT.',
    ],
  },
  {
    stage: 'Stage II', color: '#10B981',
    items: [
      'Interview (by Interviewing Officer — IO).',
      'Group Testing Officer (GTO) Tasks.',
      'Psychology Tests (by Psychologist).',
      'The Conference.',
      'Conducted over 4 days.',
    ],
  },
];

const TABS = [
  { id: 'pattern', label: 'Exam Pattern', Icon: Target },
  { id: 'english_gk', label: 'English & GK', Icon: Globe },
  { id: 'math', label: 'Mathematics', Icon: Calculator },
  { id: 'ssb', label: 'SSB Interview', Icon: Brain },
];

/* ── accordion ─────────────────────────────────────────────────────────── */
function AccordionItem({ title, points, color, isOpen, onToggle }) {
  return (
    <div className="rounded-2xl border bg-white overflow-hidden transition-shadow hover:shadow-md"
      style={{ borderColor: isOpen ? color + '40' : '#F1F5F9' }}>
      <button onClick={onToggle}
        className="w-full flex items-center gap-3 px-5 py-4 cursor-pointer border-none bg-transparent text-left">
        <div className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
        <span className="flex-1 font-bold text-sm text-slate-800">{title}</span>
        <ChevronDown size={15} className="text-slate-300 shrink-0 transition-transform"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0)' }} />
      </button>
      {isOpen && (
        <div className="px-5 pb-5 border-t" style={{ borderColor: color + '20' }}>
          <div className="mt-3 flex flex-col gap-2">
            {points.map((p, i) => (
              <div key={i} className="flex gap-3">
                <div className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" style={{ background: color }} />
                <p className="text-sm text-slate-600 leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ════════════════════════════════════════════════════════════════════════ */
export default function CDSSyllabusPage() {
  const [tab, setTab]       = useState('pattern');
  const [openMath, setOpenMath] = useState(null);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title="CDS Syllabus 2026 – Complete IMA, INA, AFA & OTA Topics | UPSC CDS II"
        description="Complete CDS 2026 syllabus from UPSC official notification. English (100 marks), General Knowledge (100 marks), Elementary Mathematics for IMA/INA/AFA (100 marks). SSB Interview 200-300 marks. Negative marking: 1/3 per wrong answer. Free topic-wise breakdown."
        keywords="CDS syllabus 2026, CDS 2 syllabus, CDS exam pattern, CDS maths syllabus, CDS English syllabus, CDS GK syllabus, UPSC CDS 2026, CDS IMA syllabus, CDS OTA syllabus, CDS SSB interview"
        url="/syllabus/cds"
        jsonLd={CDS_JSON_LD}
      />

      {/* ── Hero ── */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg,#0F172A 0%,#1a2744 55%,#1a1a3a 100%)' }}>
        <div className="absolute -top-10 -right-10 w-52 h-52 rounded-full opacity-10" style={{ background: 'radial-gradient(circle,#93C5FD,transparent)' }} />
        <div className="absolute bottom-0 left-10 w-40 h-40 rounded-full opacity-10" style={{ background: 'radial-gradient(circle,#6EE7B7,transparent)' }} />

        <div className="w-full px-6 pt-8 pb-6 relative">
          <div className="flex items-center gap-2 mb-4">
            <Shield size={13} className="text-blue-400" />
            <span className="text-xs text-blue-300 font-medium">Defence Exams</span>
            <span className="text-white/20 text-xs">›</span>
            <span className="text-xs text-white/50">CDS Syllabus 2026</span>
          </div>
          <h1 className="font-extrabold text-2xl text-white leading-tight mb-1">CDS Syllabus 2026</h1>
          <p className="text-sm text-blue-200 mb-5">Combined Defence Services Examination II · UPSC Official Syllabus</p>

          <div className="flex flex-wrap gap-2">
            {[
              { label: 'IMA / INA / AFA', value: '300', color: '#93C5FD' },
              { label: 'OTA Written',      value: '200', color: '#6EE7B7' },
              { label: 'SSB Interview',   value: '200–300', color: '#FCD34D' },
              { label: 'Papers',          value: '3 / 2',  color: '#F9A8D4' },
              { label: 'Duration each',  value: '2 hrs',   color: '#A5B4FC' },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.08)' }}>
                <span className="text-xs font-extrabold" style={{ color: s.color }}>{s.value}</span>
                <span className="text-[10px] text-white/40">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="w-full px-6 pb-0">
          <div className="flex gap-1 border-t border-white/10 pt-1">
            {TABS.map(({ id, label, Icon }) => (
              <button key={id} onClick={() => setTab(id)}
                className="flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-bold cursor-pointer border-none transition-all relative"
                style={{ background: 'transparent', color: tab === id ? '#fff' : 'rgba(255,255,255,0.38)' }}>
                <Icon size={13} />
                <span className="hidden sm:inline">{label}</span>
                {tab === id && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-0.5 rounded-full"
                    style={{ background: 'linear-gradient(90deg,#3B82F6,#10B981)' }} />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="w-full px-6 py-6 pb-16">

        {/* ══ EXAM PATTERN ══ */}
        {tab === 'pattern' && (
          <div className="flex flex-col gap-5">
            <div>
              <h2 className="font-bold text-base text-slate-800">Scheme of Examination</h2>
              <p className="text-xs text-slate-400 mt-0.5">Written test + SSB Interview — marks are equal for both stages</p>
            </div>

            {/* IMA / INA / AFA */}
            <div className="rounded-2xl border bg-white shadow-sm overflow-hidden" style={{ borderTop: '4px solid #3B82F6' }}>
              <div className="px-5 py-4 border-b border-slate-50 bg-slate-50/60">
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-blue-500">For Admission to</p>
                <h3 className="font-extrabold text-sm text-slate-800">Indian Military Academy · Indian Naval Academy · Air Force Academy</h3>
                <p className="text-xs text-slate-400 mt-0.5">Written: 300 marks + SSB Interview: 300 marks</p>
              </div>
              <div className="divide-y divide-slate-50">
                {IMA_INA_AFA.map(p => (
                  <div key={p.no} className="flex items-center gap-4 px-5 py-3">
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-extrabold shrink-0"
                      style={{ background: p.color + '18', color: p.color }}>{p.no}</div>
                    <div className="flex-1">
                      <p className="font-bold text-sm text-slate-800">{p.subject}</p>
                      <p className="text-xs text-slate-400">Code {p.code} · {p.duration}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-extrabold" style={{ color: p.color }}>{p.marks}</p>
                      <p className="text-[10px] text-slate-300">marks</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* OTA */}
            <div className="rounded-2xl border bg-white shadow-sm overflow-hidden" style={{ borderTop: '4px solid #10B981' }}>
              <div className="px-5 py-4 border-b border-slate-50 bg-slate-50/60">
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-emerald-500">For Admission to</p>
                <h3 className="font-extrabold text-sm text-slate-800">Officers' Training Academy (OTA)</h3>
                <p className="text-xs text-slate-400 mt-0.5">Written: 200 marks + SSB Interview: 200 marks</p>
              </div>
              <div className="divide-y divide-slate-50">
                {OTA.map(p => (
                  <div key={p.no} className="flex items-center gap-4 px-5 py-3">
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-extrabold shrink-0"
                      style={{ background: p.color + '18', color: p.color }}>{p.no}</div>
                    <div className="flex-1">
                      <p className="font-bold text-sm text-slate-800">{p.subject}</p>
                      <p className="text-xs text-slate-400">Code {p.code} · {p.duration}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-extrabold" style={{ color: p.color }}>{p.marks}</p>
                      <p className="text-[10px] text-slate-300">marks</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Standard note */}
            <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4">
              <p className="text-xs font-bold text-amber-700 mb-1">📚 Standard of Papers</p>
              <p className="text-xs text-amber-700 leading-relaxed">
                The standard of papers in <strong>Elementary Mathematics</strong> will be of <strong>Matriculation level</strong>.
                The standard of papers in other subjects will approximately be such as may be expected of a <strong>graduate of an Indian University</strong>.
              </p>
            </div>

            {/* Negative marking */}
            <div className="rounded-2xl border border-red-100 bg-red-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={14} className="text-red-500" />
                <p className="text-xs font-bold text-red-700">Negative Marking</p>
              </div>
              <p className="text-xs text-red-600 leading-relaxed">
                <strong>1/3 mark deducted</strong> for each wrong answer. No penalty for unattempted questions. Marking more than one response counts as wrong.
              </p>
            </div>

            {/* Key rules */}
            <div>
              <h3 className="font-bold text-sm text-slate-700 mb-3">Key Instructions</h3>
              <div className="flex flex-col gap-2.5">
                {[
                  'All papers consist of Objective Type (MCQ) questions only.',
                  'Question papers of General Knowledge and Elementary Mathematics will be set bilingually in Hindi as well as English.',
                  'Questions involving the metric system of Weights and Measures only will be set.',
                  'Candidates must write the papers in their own hand — use of a scribe is not permitted.',
                  'The Commission has discretion to fix qualifying marks in any or all subjects.',
                  'Calculators, mathematical or logarithmic tables are NOT permitted inside the Examination Hall.',
                ].map((rule, i) => (
                  <div key={i} className="flex gap-3 bg-white border border-slate-100 rounded-2xl px-4 py-3 shadow-sm">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-extrabold shrink-0 mt-0.5"
                      style={{ background: '#EFF6FF', color: '#3B82F6' }}>{i + 1}</div>
                    <p className="text-sm text-slate-600 leading-relaxed">{rule}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ══ ENGLISH & GK ══ */}
        {tab === 'english_gk' && (
          <div className="flex flex-col gap-5">
            <div>
              <h2 className="font-bold text-base text-slate-800">English & General Knowledge</h2>
              <p className="text-xs text-slate-400 mt-0.5">Both papers — 100 marks each · 2 hours each</p>
            </div>

            {/* English */}
            <div className="rounded-2xl border bg-white shadow-sm overflow-hidden" style={{ borderTop: '4px solid #10B981' }}>
              <div className="px-5 py-4 border-b border-slate-50 bg-emerald-50/40">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-emerald-100">
                      <Languages size={15} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-[10px] font-extrabold uppercase tracking-widest text-emerald-600">Code 11</p>
                      <h3 className="font-extrabold text-sm text-slate-800">English</h3>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-extrabold text-emerald-600">100</p>
                    <p className="text-[10px] text-slate-400">marks</p>
                  </div>
                </div>
              </div>
              <div className="px-5 py-4 flex flex-col gap-2">
                {[
                  'The question paper will be designed to test the candidates\' understanding of English and workmanlike use of words.',
                  'Grammar and usage.',
                  'Vocabulary.',
                  'Comprehension.',
                  'Cohesion in extended text.',
                  'Overall proficiency in English — graduate level.',
                ].map((p, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 bg-emerald-400" />
                    <p className="text-sm text-slate-600 leading-relaxed">{p}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* General Knowledge */}
            <div className="rounded-2xl border bg-white shadow-sm overflow-hidden" style={{ borderTop: '4px solid #6366F1' }}>
              <div className="px-5 py-4 border-b border-slate-50 bg-indigo-50/40">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-indigo-100">
                      <Globe size={15} className="text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-[10px] font-extrabold uppercase tracking-widest text-indigo-600">Code 12</p>
                      <h3 className="font-extrabold text-sm text-slate-800">General Knowledge</h3>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-extrabold text-indigo-600">100</p>
                    <p className="text-[10px] text-slate-400">marks</p>
                  </div>
                </div>
              </div>
              <div className="px-5 py-4 flex flex-col gap-2">
                {[
                  'General Knowledge including knowledge of current events.',
                  'Matters of everyday observation and experience in their scientific aspects as may be expected of an educated person who has not made a special study of any scientific subject.',
                  'History of India — questions of a nature which the candidate should be able to answer without special study.',
                  'Geography — questions of a nature which the candidate should be able to answer without special study.',
                ].map((p, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 bg-indigo-400" />
                    <p className="text-sm text-slate-600 leading-relaxed">{p}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* OTA note */}
            <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
              <p className="text-xs font-bold text-blue-700 mb-1">ℹ️ OTA Candidates</p>
              <p className="text-xs text-blue-600 leading-relaxed">
                OTA (Officers' Training Academy) candidates appear in <strong>English and General Knowledge only</strong>. Elementary Mathematics is not required for OTA.
              </p>
            </div>
          </div>
        )}

        {/* ══ MATHEMATICS ══ */}
        {tab === 'math' && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-bold text-base text-slate-800">Elementary Mathematics</h2>
                <p className="text-xs text-slate-400 mt-0.5">Code 13 · 100 Marks · 2 Hours · Matriculation level · IMA / INA / AFA only</p>
              </div>
              <button onClick={() => setOpenMath(openMath === 'all' ? null : 'all')}
                className="text-xs font-bold text-amber-600 px-3 py-1.5 rounded-xl bg-amber-50 border-none cursor-pointer shrink-0">
                {openMath === 'all' ? 'Collapse all' : 'Expand all'}
              </button>
            </div>

            {MATH_TOPICS.map(topic => (
              <AccordionItem
                key={topic.id}
                title={topic.title}
                points={topic.points}
                color={topic.color}
                isOpen={openMath === topic.id || openMath === 'all'}
                onToggle={() => setOpenMath(prev => prev === topic.id ? null : topic.id)}
              />
            ))}

            <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4 mt-1">
              <p className="text-xs font-bold text-amber-700 mb-1">📌 Note</p>
              <p className="text-xs text-amber-700 leading-relaxed">Elementary Mathematics is only required for IMA, INA and AFA. OTA candidates do NOT appear in this paper.</p>
            </div>
          </div>
        )}

        {/* ══ SSB INTERVIEW ══ */}
        {tab === 'ssb' && (
          <div className="flex flex-col gap-5">
            <div>
              <h2 className="font-bold text-base text-slate-800">Intelligence & Personality Test (SSB)</h2>
              <p className="text-xs text-slate-400 mt-0.5">Services Selection Board · Two-stage selection process</p>
            </div>

            {/* marks by course */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { course: 'IMA', marks: 300, color: '#6366F1' },
                { course: 'INA', marks: 300, color: '#3B82F6' },
                { course: 'AFA', marks: 300, color: '#8B5CF6' },
                { course: 'OTA', marks: 200, color: '#10B981' },
              ].map(c => (
                <div key={c.course} className="rounded-2xl border bg-white p-4 shadow-sm flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-extrabold uppercase tracking-widest" style={{ color: c.color }}>{c.course}</p>
                    <p className="font-bold text-sm text-slate-700">SSB Interview</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-extrabold" style={{ color: c.color }}>{c.marks}</p>
                    <p className="text-[10px] text-slate-300">marks</p>
                  </div>
                </div>
              ))}
            </div>

            {/* stages */}
            {SSB_STAGES.map(s => (
              <div key={s.stage} className="rounded-2xl border bg-white shadow-sm overflow-hidden"
                style={{ borderTop: `4px solid ${s.color}` }}>
                <div className="px-5 py-4 border-b border-slate-50" style={{ background: s.color + '08' }}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center font-extrabold text-xs"
                      style={{ background: s.color + '18', color: s.color }}>
                      {s.stage === 'Stage I' ? 'I' : 'II'}
                    </div>
                    <h3 className="font-extrabold text-sm text-slate-800">{s.stage}</h3>
                  </div>
                </div>
                <div className="px-5 py-4 flex flex-col gap-2">
                  {s.items.map((item, i) => (
                    <div key={i} className="flex gap-3">
                      <CheckCircle size={14} className="shrink-0 mt-0.5" style={{ color: s.color }} />
                      <p className="text-sm text-slate-600 leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Assessors */}
            <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-5">
              <div className="flex items-center gap-2 mb-4">
                <Users size={15} className="text-slate-500" />
                <h3 className="font-bold text-sm text-slate-800">Three Independent Assessors</h3>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Equal Weightage</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { title: 'IO', full: 'Interviewing Officer', color: '#6366F1' },
                  { title: 'GTO', full: 'Group Testing Officer', color: '#10B981' },
                  { title: 'Psych', full: 'Psychologist', color: '#F59E0B' },
                ].map(a => (
                  <div key={a.title} className="text-center p-3 rounded-xl" style={{ background: a.color + '10' }}>
                    <p className="text-base font-extrabold" style={{ color: a.color }}>{a.title}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5 leading-tight">{a.full}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-4 leading-relaxed">
                The personality of a candidate is assessed holistically by all three assessors. There are no separate weightages for each test. Marks are allotted only after taking into consideration the candidate's performance across all tests. All three have <strong>equal weightage</strong>.
              </p>
              <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                The tests are designed to bring out the presence/absence of <strong>Officer Like Qualities (OLQs)</strong> and their trainability. Candidates are Recommended or Not Recommended at the SSB.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
